'use client'

import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocale, useTranslations } from 'next-intl'
import { DayPicker, type Matcher } from 'react-day-picker'
import { format, isValid, parseISO } from 'date-fns'
import { enGB, ro, ru, uk, type Locale } from 'date-fns/locale'
import { Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClickOutside } from '@/lib/utils/use-click-outside'
import { useMounted } from '@/lib/hooks/use-mounted'
import { fieldClass, openFieldClass, type FieldVariant } from '@/components/ui/field-styles'
import { useAnchoredPanel, panelClass, PANEL_ATTR } from '@/components/ui/floating'
import { Select } from '@/components/ui/Select'
import { useFieldLabel } from '@/components/ui/field-label'

// ============================================================================
// ВЫБОР ДАТЫ — ОДИН НА ПРОЕКТ.
// ----------------------------------------------------------------------------
// Вместо <input type="date">: его календарь рисует браузер (синие квадраты,
// стрелки вверх-вниз, своя кнопка «Удалить»), и в каждом браузере он свой.
// Здесь — react-day-picker в нашем виде: язык интерфейса, неделя с
// понедельника, выбранный день акцентом, «Сегодня» и «Очистить» внизу.
//
// Значение — та же строка, что давал нативный input: `yyyy-MM-dd`, а с
// `withTime` — `yyyy-MM-ddTHH:mm` (как datetime-local). Поэтому замена не
// трогает ни состояние формы, ни то, что уходит на сервер.
// ============================================================================

const LOCALES: Record<string, Locale> = { ru, ro, uk, en: enGB }

const DATE = 'yyyy-MM-dd'
const DATE_TIME = "yyyy-MM-dd'T'HH:mm"

function parseValue(v: string | null | undefined): Date | undefined {
  if (!v) return undefined
  const d = parseISO(v)
  return isValid(d) ? d : undefined
}

const pad = (n: number) => String(n).padStart(2, '0')
const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: pad(h), label: pad(h) }))

export function DatePicker({
  value, onChange, withTime = false, placeholder, disabled, variant = 'field', error, className,
  name, id, min, max, clearable = true, required, onClose, title, 'data-field': dataField, 'aria-label': ariaLabel,
}: {
  value: string | null | undefined
  /** '' — дата снята. */
  onChange: (value: string) => void
  withTime?: boolean
  placeholder?: string
  disabled?: boolean
  variant?: FieldVariant
  error?: boolean
  className?: string
  name?: string
  id?: string
  /** Границы выбора, в том же формате, что value. */
  min?: string
  max?: string
  /** Можно ли снять дату. Для обязательного поля — false. */
  clearable?: boolean
  required?: boolean
  /** Панель закрылась — с последним значением (для «сохранить при уходе с поля»). */
  onClose?: (value: string) => void
  title?: string
  /** Метка поля для «Что дальше» — на самом поле, к нему прокручивает фокус. */
  'data-field'?: string
  'aria-label'?: string
}) {
  const t = useTranslations()
  const localeCode = useLocale()
  const locale = LOCALES[localeCode] ?? enGB
  const mounted = useMounted()
  const { labelledBy } = useFieldLabel({ 'aria-label': ariaLabel })
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const { refs, floatingStyles } = useAnchoredPanel(open, { matchWidth: false, maxHeight: 520 })

  const date = parseValue(value)
  const [month, setMonth] = useState<Date | undefined>(date)

  // Последнее отданное значение: onClose зовут сразу после onChange, когда
  // родитель ещё не успел прислать новый value.
  const lastRef = useRef(value ?? '')
  lastRef.current = value ?? ''
  const close = (refocus = true) => {
    setOpen(false)
    onClose?.(lastRef.current)
    if (refocus) triggerRef.current?.focus()
  }
  useClickOutside(panelRef, () => close(false), { active: open, extraRefs: [triggerRef] })

  const emit = (d: Date | undefined) => {
    const v = d ? format(d, withTime ? DATE_TIME : DATE) : ''
    lastRef.current = v
    onChange(v)
  }

  const pickDay = (d: Date | undefined) => {
    if (!d) return
    if (withTime) {
      // Время дня не теряется при смене даты; у пустого поля — полдень.
      const next = new Date(d)
      next.setHours(date ? date.getHours() : 12, date ? date.getMinutes() : 0, 0, 0)
      emit(next)
    } else {
      emit(d)
      close()
    }
  }

  const setTime = (h: number, m: number) => {
    const base = date ?? new Date()
    const next = new Date(base)
    next.setHours(h, m, 0, 0)
    emit(next)
  }

  const minutes = useMemo(() => {
    const list = Array.from({ length: 12 }, (_, i) => pad(i * 5))
    const cur = date ? pad(date.getMinutes()) : null
    // Минуты не кратные пяти (сохранены раньше) остаются в списке.
    if (cur && !list.includes(cur)) list.push(cur)
    return list.sort().map(m => ({ value: m, label: m }))
  }, [date])

  const disabledDays = useMemo(() => {
    const out: Matcher[] = []
    const lo = parseValue(min)
    const hi = parseValue(max)
    if (lo) out.push({ before: lo })
    if (hi) out.push({ after: hi })
    return out
  }, [min, max])

  const text = date ? format(date, withTime ? 'P HH:mm' : 'P', { locale }) : ''

  return (
    <>
      <button
        ref={node => { triggerRef.current = node; refs.setReference(node) }}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        // Выбранная дата — для обучения: оно видит, что поле заполнено (components/tours/form-walk.ts).
        data-value={value ?? ''}
        title={title}
        data-field={dataField}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (open) { close(); return }
          setMonth(date ?? new Date())
          setOpen(true)
        }}
        onKeyDown={e => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setMonth(date ?? new Date()); setOpen(true) } }}
        className={cn(
          fieldClass(error, variant),
          'flex items-center gap-2 text-left tabular-nums',
          variant === 'compact' ? 'pr-1.5' : 'pr-2.5',
          open && openFieldClass(variant),
          className,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !text && 'text-ink-400')}>
          {text || placeholder || t('ui.picker.datePlaceholder')}
        </span>
        <Calendar size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-ink-500" />
      </button>
      {name && <input type="hidden" name={name} value={value ?? ''} required={required} />}

      {open && mounted && createPortal(
        <div
          ref={node => { panelRef.current = node; refs.setFloating(node) }}
          {...{ [PANEL_ATTR]: '' }}
          role="dialog"
          aria-label={ariaLabel ?? t('ui.picker.datePlaceholder')}
          style={floatingStyles}
          className={cn(panelClass, 'overflow-y-auto')}
          onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close() } }}
        >
          <DayPicker
            mode="single"
            locale={locale}
            weekStartsOn={1}
            selected={date}
            onSelect={pickDay}
            month={month}
            onMonthChange={setMonth}
            disabled={disabledDays}
            showOutsideDays
            autoFocus
            classNames={{
              root: 'p-3 text-[13px] text-ink-900',
              months: 'relative',
              month: 'space-y-2',
              month_caption: 'flex h-8 items-center pl-1 pr-20 text-[14px] font-bold capitalize',
              caption_label: 'truncate',
              nav: 'absolute right-0 top-0 flex items-center gap-0.5',
              button_previous: 'inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-ink-50 hover:text-ink-900 disabled:opacity-30',
              button_next: 'inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-ink-50 hover:text-ink-900 disabled:opacity-30',
              chevron: 'h-4 w-4 fill-current',
              month_grid: 'border-collapse',
              weekdays: '',
              weekday: 'h-8 w-9 text-[11px] font-semibold uppercase text-ink-400',
              week: '',
              day: 'p-0 text-center',
              day_button:
                'h-9 w-9 rounded-md text-[13px] tabular-nums text-ink-800 transition-colors hover:bg-ink-50 ' +
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
              today: '[&>button]:font-bold [&>button]:text-accent-600',
              selected: '[&>button]:!bg-accent-500 [&>button]:!text-white [&>button]:font-semibold',
              outside: '[&>button]:text-ink-300',
              disabled: '[&>button]:cursor-not-allowed [&>button]:text-ink-300 [&>button]:hover:bg-transparent',
              hidden: 'invisible',
            }}
          />

          {withTime && (
            <div className="flex items-center gap-2 border-t-hairline border-ink-200 px-3 py-2.5">
              <span className="text-[12px] font-semibold text-ink-500">{t('ui.picker.time')}</span>
              <div className="ml-auto flex items-center gap-1">
                <Select
                  aria-label={t('ui.picker.hours')}
                  value={date ? pad(date.getHours()) : ''}
                  onChange={h => setTime(Number(h), date ? date.getMinutes() : 0)}
                  options={HOURS}
                  searchable={false}
                  placeholder="--"
                  className="w-[68px] py-1.5"
                />
                <span className="text-ink-400">:</span>
                <Select
                  aria-label={t('ui.picker.minutes')}
                  value={date ? pad(date.getMinutes()) : ''}
                  onChange={m => setTime(date ? date.getHours() : 12, Number(m))}
                  options={minutes}
                  searchable={false}
                  placeholder="--"
                  className="w-[68px] py-1.5"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border-t-hairline border-ink-200 px-3 py-2">
            {clearable ? (
              <button
                type="button"
                onClick={() => { emit(undefined); close() }}
                className="rounded-md px-2 py-1 text-[12.5px] font-semibold text-ink-500 hover:bg-ink-50 hover:text-ink-900"
              >
                {t('ui.picker.clear')}
              </button>
            ) : <span />}
            <button
              type="button"
              onClick={() => {
                const now = new Date()
                if (withTime && date) now.setHours(date.getHours(), date.getMinutes(), 0, 0)
                setMonth(now)
                pickDay(now)
              }}
              className="rounded-md px-2 py-1 text-[12.5px] font-semibold text-accent-600 hover:bg-accent-50"
            >
              {t('ui.picker.today')}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

/** Событие в форме нативного: старые обработчики читают `e.target.value`. */
export interface DateInputEvent {
  target: { value: string; name?: string }
  currentTarget: { value: string; name?: string }
}

/**
 * DatePicker с подписью нативного `<input type="date|datetime-local">`:
 * `onChange(e)` с `e.target.value`, `onBlur` — когда панель закрылась.
 * Нужен, чтобы старое поле даты переводилось заменой тега, без переписывания
 * обработчиков. В новом коде — DatePicker с `onChange(value)`.
 */
export function DateInput({
  type = 'date', value, defaultValue, onChange, onBlur, readOnly, disabled, className, variant, ...rest
}: {
  type?: 'date' | 'datetime-local'
  value?: string | null
  /** Неуправляемое поле (форма читает его через name). */
  defaultValue?: string | null
  onChange?: (e: DateInputEvent) => void
  onBlur?: (e: DateInputEvent) => void
  readOnly?: boolean
  disabled?: boolean
  className?: string
  variant?: FieldVariant
  placeholder?: string
  name?: string
  id?: string
  min?: string
  max?: string
  required?: boolean
  error?: boolean
  title?: string
  'data-field'?: string
  'aria-label'?: string
}) {
  const [own, setOwn] = useState(defaultValue ?? '')
  const current = value !== undefined ? value : own
  const ev = (v: string): DateInputEvent => ({ target: { value: v, name: rest.name }, currentTarget: { value: v, name: rest.name } })
  return (
    <DatePicker
      {...rest}
      value={current}
      withTime={type === 'datetime-local'}
      disabled={disabled || readOnly}
      clearable={!rest.required}
      variant={variant}
      // Нативное поле без ширины было по содержимому, а не во всю строку.
      className={/(^|\s)([a-z]+:)?w-/.test(className ?? '')
        ? className
        : cn(type === 'datetime-local' ? 'w-auto min-w-[11.5rem]' : 'w-auto min-w-[8.5rem]', className)}
      onChange={v => {
        if (value === undefined) setOwn(v)
        onChange?.(ev(v))
      }}
      onClose={onBlur ? v => onBlur(ev(v)) : undefined}
    />
  )
}
